import React from 'react';
import { AlertCircle, Loader2, Database } from 'lucide-react';
import { isMissingObject } from '@/lib/schemaReady';

// Full-panel state when the module's database objects are not installed yet. No records are shown or fabricated.
export const MigrationPending = ({ module, migration, testId = 'migration-pending', children }) => (
  <div data-testid={testId} className="rounded-lg border border-border bg-white p-8 md:p-10">
    <div className="flex items-start gap-4">
      <div className="h-11 w-11 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: '#EFECE6', color: '#8A877F' }}><Database size={20} /></div>
      <div className="max-w-2xl">
        <div className="font-display font-semibold text-charcoal text-base md:text-lg">Awaiting database migration</div>
        <p className="mt-1 text-sm text-muted-foreground">
          {module} is built, but its database objects are not yet installed in this Supabase project. It activates once <code className="text-charcoal">{migration}</code> has been reviewed and executed by the Owner. No records are displayed and nothing can be saved until then.
        </p>
        {children}
      </div>
    </div>
  </div>
);

// Pass the result of useSchemaReady(); renders the right gate or returns null when ready.
export const ReadinessGate = ({ readiness, module, migration, testId }) => {
  if (readiness.state === 'checking') return <Loading label="Checking database…" />;
  if (readiness.state === 'pending') return <MigrationPending module={module} migration={migration} testId={testId} />;
  if (readiness.state === 'error') return <SchemaNotice error={readiness.error} testId={`${testId}-error`} />;
  return null;
};

// Shared states for F-1 pages: loading, "schema not installed yet", generic error.
export const SchemaNotice = ({ error, migration = 'f1_foundation_migration.sql', testId = 'schema-notice' }) => {
  const missing = isMissingObject(error);
  return (
    <div data-testid={testId} className={`rounded-md border px-4 py-3 text-sm flex items-start gap-2 ${missing ? 'border-border bg-white text-charcoal' : 'border-red-200 bg-red-50 text-red-800'}`}>
      <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
      <div>
        {missing ? (
          <>
            <div className="font-medium">Awaiting database migration</div>
            <div className="mt-0.5 text-muted-foreground">This module is built and will activate once <code>{migration}</code> has been executed in Supabase.</div>
          </>
        ) : error?.message}
      </div>
    </div>
  );
};

export const Loading = ({ label = 'Loading…' }) => (
  <div className="p-6 flex items-center gap-2 text-sm text-muted-foreground"><Loader2 size={14} className="animate-spin" /> {label}</div>
);

export const Field = ({ label, children, hint }) => (
  <label className="block">
    <span className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</span>
    <div className="mt-1">{children}</div>
    {hint && <div className="mt-1 text-[11px] text-muted-foreground">{hint}</div>}
  </label>
);

export const StatusPill = ({ value, testId }) => {
  const tone = {
    pending: 'border-border text-charcoal', escalated: 'border-amber-300 text-amber-800', approved: 'border-emerald-300 text-emerald-800',
    rejected: 'border-red-300 text-red-800', cancelled: 'border-border text-muted-foreground', active: 'border-emerald-300 text-emerald-800',
    on_hold: 'border-amber-300 text-amber-800', inactive: 'border-border text-muted-foreground', verified: 'border-emerald-300 text-emerald-800',
    unverified: 'border-border text-muted-foreground',
  }[value] || 'border-border text-charcoal';
  return <span data-testid={testId} className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] uppercase tracking-wider bg-white ${tone}`}>{String(value || '').replace('_', ' ')}</span>;
};
